import { useCallback, useEffect, useRef, useState } from "react";

// Independent v2 storage — does NOT touch the live guide's progress key.
const KEY = "routehaul_guide_v2";

const EMPTY = { completed: [], worksheets: {}, actions: {} };

function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    if (!raw || typeof raw !== "object") return { ...EMPTY };
    return {
      completed: Array.isArray(raw.completed) ? raw.completed : [],
      worksheets: raw.worksheets && typeof raw.worksheets === "object" ? raw.worksheets : {},
      actions: raw.actions && typeof raw.actions === "object" ? raw.actions : {},
    };
  } catch {
    return { ...EMPTY };
  }
}

const EVENT = "routehaul-guide-v2";

export function useGuideV2Progress() {
  const [data, setData] = useState(read);
  const ref = useRef(data);

  useEffect(() => {
    ref.current = data;
  }, [data]);

  useEffect(() => {
    const sync = () => setData(read());
    window.addEventListener("storage", sync);
    window.addEventListener(EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(EVENT, sync);
    };
  }, []);

  const persist = useCallback((next) => {
    ref.current = next;
    setData(next);
    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(EVENT));
  }, []);

  const toggleModule = useCallback((n) => {
    const cur = ref.current;
    const completed = cur.completed.includes(n)
      ? cur.completed.filter((x) => x !== n)
      : [...cur.completed, n];
    persist({ ...cur, completed });
  }, [persist]);

  const setField = useCallback((n, id, value) => {
    const cur = ref.current;
    const worksheets = { ...cur.worksheets, [n]: { ...(cur.worksheets[n] || {}), [id]: value } };
    persist({ ...cur, worksheets });
  }, [persist]);

  const toggleAction = useCallback((n, i) => {
    const cur = ref.current;
    const modActions = { ...(cur.actions[n] || {}) };
    modActions[i] = !modActions[i];
    persist({ ...cur, actions: { ...cur.actions, [n]: modActions } });
  }, [persist]);

  const actionProgress = useCallback((n, total) => {
    const modActions = data.actions[n] || {};
    const done = Object.values(modActions).filter(Boolean).length;
    return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [data]);

  const worksheetFilled = useCallback((n) => {
    const w = data.worksheets[n] || {};
    return Object.values(w).filter((v) => String(v || "").trim() !== "").length;
  }, [data]);

  return {
    data,
    completed: data.completed,
    count: data.completed.length,
    isDone: (n) => data.completed.includes(n),
    toggleModule,
    getField: (n, id) => data.worksheets[n]?.[id] ?? "",
    setField,
    isActionDone: (n, i) => !!data.actions[n]?.[i],
    toggleAction,
    actionProgress,
    worksheetFilled,
  };
}
